/**
 * Shared medication formatting utilities.
 * Extracted from CaseSheetView to avoid pulling UI code into non-UI utils.
 */

import type { PrescribedMedicine } from '@/ayurveda/types/ayurveda';

export interface StructuredMedication {
  medication: string;
  dosage: string;
  frequency: string;
  timing: string[];
  duration: string;
  route?: string;
  relationWithMeal?: string;
  additionalInfo?: string;
  stock?: string;
  itemCode?: string;
  medicineType?: string;
  composition?: string;
  dosingType?: string;
  sosTrigger?: string;
  maxPerDay?: string;
}

/**
 * Convert any medication input to a StructuredMedication.
 * Handles: StructuredMedication objects, legacy string format, raw AI objects.
 */
export function toStructuredMedication(med: unknown): StructuredMedication | null {
  if (!med) return null;

  const toDisplayString = (v: unknown): string => {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    return String(v);
  };

  const normalizeStock = (v: unknown): string | undefined => {
    const raw = toDisplayString(v).trim();
    if (!raw) return undefined;
    const normalized = raw.toLowerCase();
    if (normalized === 'na' || normalized === 'n/a') return undefined;
    return raw;
  };

  const getValue = (v: unknown): string => {
    if (typeof v === 'object' && v !== null && 'value' in v) {
      return toDisplayString((v as Record<string, unknown>).value);
    }
    return toDisplayString(v);
  };

  const convertFrequencyToSchedule = (freq: string): string => {
    if (!freq) return '';
    const lower = freq.toLowerCase().trim();

    if (/^\d+-\d+(-\d+)*$/.test(lower)) return freq;

    if (/\b(three times|3 times|thrice)\b/.test(lower)) return '1-1-1';
    if (/\b(four times|4 times)\b/.test(lower)) return '1-1-1-1';
    if (/\b(twice|two times|2 times|bd|b\.d)\b/.test(lower)) return '1-0-1';
    if (/\b(once|one time|1 time|od|o\.d|daily)\b/.test(lower)) return '0-0-1';
    if (/\b(as needed|sos|prn)\b/.test(lower)) return 'SOS';
    if (/\bevery\b/.test(lower)) return freq;
    if (/\bmorning\b/.test(lower) && /\bnight\b/.test(lower)) return '1-0-1';
    if (/\bmorning\b/.test(lower)) return '1-0-0';
    if (/\b(night|bedtime|hs)\b/.test(lower)) return '0-0-1';
    if (/\bafternoon\b/.test(lower)) return '0-1-0';

    return freq;
  };

  if (typeof med === 'object' && med !== null) {
    const m = med as Record<string, unknown>;
    const medicationName = getValue(m.medication ?? m.name);
    if (!medicationName) return null;

    return {
      medication: medicationName,
      dosage: (() => {
        const d = m.dosage;
        if (typeof d === 'string') return d;
        if (typeof d === 'object' && d !== null && 'value' in d) {
          const obj = d as Record<string, unknown>;
          const val = toDisplayString(obj.value);
          const unit = toDisplayString(obj.unit);
          return [val, unit].filter((s) => s !== '').join(' ');
        }
        return getValue(m.dosage);
      })(),
      frequency: convertFrequencyToSchedule(getValue(m.frequency ?? m.period)),
      timing: Array.isArray(m.timing)
        ? m.timing.map(String)
        : typeof m.timing === 'string'
          ? m.timing
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      duration: getValue(m.duration),
      route: getValue(m.route ?? m.intake_type),
      relationWithMeal: getValue(m.relation_with_meal ?? m.relationWithMeal),
      additionalInfo: getValue(m.additional_info ?? m.additionalInfo ?? m.comments),
      stock: normalizeStock(m.stock),
      itemCode: getValue(m.item_code ?? m.itemCode),
      medicineType: getValue(m.medicine_type ?? m.medicineType),
      composition: getValue(m.composition) || undefined,
      dosingType: getValue(m.dosing_type ?? m.dosingType) || undefined,
      sosTrigger: getValue(m.sos_trigger ?? m.sosTrigger) || undefined,
      maxPerDay: getValue(m.max_per_day ?? m.maxPerDay) || undefined,
    };
  }

  if (typeof med === 'string') {
    const trimmed = med.trim();
    if (!trimmed) return null;
    return {
      medication: trimmed,
      dosage: '',
      frequency: '',
      timing: [],
      duration: '',
      route: '',
      relationWithMeal: '',
      additionalInfo: '',
      medicineType: '',
    };
  }

  return null;
}

/**
 * Rebuild an Ayurveda Medicines-table row from a saved medication entry.
 * Saved reports sometimes carry the AI's `prescribed_medicines` rows under the
 * generic `medications` key — toStructuredMedication() would drop their
 * dose/form/anupana. Returns null for anything that isn't in that shape
 * (generic entries use `dosage`/`medication`, never `dose`/`anupana`). `form`
 * is shared with generic entries (e.g. "Tablet"), so it can't be the marker.
 */
export function toPrescribedMedicine(med: unknown): PrescribedMedicine | null {
  if (!med || typeof med !== 'object') return null;
  const obj = med as Record<string, unknown>;
  if (!['anupana', 'dose'].some((key) => key in obj)) return null;

  const asString = (v: unknown): string | null => {
    const raw =
      typeof v === 'object' && v !== null && 'value' in v ? (v as { value: unknown }).value : v;
    if (Array.isArray(raw)) return raw.map(String).join(', ') || null;
    return typeof raw === 'string' && raw.trim() ? raw : null;
  };

  const name = asString(obj.name ?? obj.medication);
  if (!name) return null;
  return {
    name,
    form: asString(obj.form),
    dose: asString(obj.dose),
    anupana: asString(obj.anupana),
    timing: asString(obj.timing),
    frequency: asString(obj.frequency),
    duration: asString(obj.duration),
    additional_info: asString(obj.additional_info),
  };
}

/**
 * Format a StructuredMedication as a display string (fallback for legacy contexts).
 */
export function formatMedicationString(med: StructuredMedication): string {
  const parts = [med.medication, med.dosage, med.frequency, med.duration].filter(Boolean);
  if (med.timing.length > 0) {
    parts.push(med.timing.join(', '));
  }
  return parts.join(' - ');
}
